'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConventionTagEditor } from '../app/(sidebar)/conventions/detail/ConventionTagEditor';
import { useProject, useUpdateProjectMutation } from '../hooks/queries/useTeams';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import { fetchProjectMembers, fetchTeamMembers, type ProjectMember, type TeamMember } from '../lib/api';
import { getAuthSession } from '../lib/authSession';
import { canManageTeamMembers, findCurrentTeamMembership } from '../lib/permissions';
import { CollapsibleSection } from './CollapsibleSection';

type ProjectTagsViewProps = {
  projectId: string;
};

export default function ProjectTagsView({ projectId }: ProjectTagsViewProps) {
  const t = useTranslations('projects.tags');
  const tRoot = useTranslations();
  const session = useMemo(() => getAuthSession()!, []);
  const projectQuery = useProject(session.token, projectId);
  const updateProjectMutation = useUpdateProjectMutation(session.token);

  const [tags, setTags] = useState<string[]>([]);
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isPermissionLoading, setIsPermissionLoading] = useState(true);

  useEffect(() => {
    if (projectQuery.data) {
      setTags(projectQuery.data.tags);
    }
  }, [projectQuery.data]);

  useEffect(() => {
    if (!projectId) {
      toast.error(t('toastProjectIdMissing'));
      setIsPermissionLoading(false);
      return;
    }

    if (!projectQuery.data) {
      return;
    }

    const loadPermissions = async () => {
      setIsPermissionLoading(true);
      try {
        const [foundProjectMembers, foundTeamMembers] = await Promise.all([
          fetchProjectMembers(session.token, projectId),
          fetchTeamMembers(session.token, projectQuery.data.teamId),
        ]);
        setProjectMembers(foundProjectMembers);
        setTeamMembers(foundTeamMembers);
      } catch (loadError) {
        const message = resolveApiErrorToastMessage(tRoot, loadError);
        if (message) toast.error(message);
      } finally {
        setIsPermissionLoading(false);
      }
    };

    void loadPermissions();
  }, [projectId, projectQuery.data, session.token, t, tRoot]);

  const currentTeamMembership = useMemo(() => {
    return findCurrentTeamMembership(teamMembers, session.member.id);
  }, [session.member.id, teamMembers]);

  const currentProjectMembership = useMemo(() => {
    return projectMembers.find((member) => member.memberId === session.member.id);
  }, [projectMembers, session.member.id]);

  const canManage =
    canManageTeamMembers(currentTeamMembership?.role) || canManageTeamMembers(currentProjectMembership?.role);
  const isLoading = projectQuery.isLoading || isPermissionLoading;
  const savedTags = projectQuery.data?.tags ?? [];
  const hasChanges = tags.length !== savedTags.length || tags.some((tag, index) => tag !== savedTags[index]);

  const handleSave = async () => {
    if (!projectQuery.data) {
      return;
    }

    if (!canManage) {
      toast.error(t('toastPermissionManage'));
      return;
    }

    try {
      const updated = await updateProjectMutation.mutateAsync({
        projectId,
        teamId: projectQuery.data.teamId,
        input: { tags },
      });
      setTags(updated.tags);
      toast.success(t('toastSaved'));
    } catch (saveError) {
      const message = resolveApiErrorToastMessage(tRoot, saveError);
      if (message) toast.error(message);
    }
  };

  if (projectQuery.isError) {
    return <div className="rounded-xl border border-error/30 bg-error/5 p-4 text-sm text-error">{t('loadFailed')}</div>;
  }

  return (
    <div className="space-y-4">
      {!isLoading && !canManage ? <p className="text-xs text-warning">{t('permissionHint')}</p> : null}

      <CollapsibleSection title={t('sectionTitle')} description={t('sectionDescription')}>
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={isLoading || !canManage || !hasChanges || updateProjectMutation.isPending}
            onClick={() => void handleSave()}
          >
            {updateProjectMutation.isPending ? t('saving') : t('save')}
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <div className="skeleton h-8 w-full rounded-lg" />
            <div className="skeleton h-20 w-full rounded-xl" />
          </div>
        ) : (
          <ConventionTagEditor
            value={tags}
            onChange={setTags}
            disabled={!canManage || updateProjectMutation.isPending}
            maxTags={32}
            placeholder={t('tagInputPlaceholder')}
          />
        )}
      </CollapsibleSection>
    </div>
  );
}
